import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export interface IUseCountdown {
  isStarted: boolean;
  isFinished: boolean;
  isCounting: boolean;
  currentTime: number;
  start: () => void;
}

type CountdownState = Pick<
  IUseCountdown,
  "isStarted" | "isFinished" | "isCounting" | "currentTime"
>;

export const useCountdown = (time: number): IUseCountdown => {
  const intervalTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isCountingRef = useRef(false);
  const normalizedTime = useMemo(() => Math.max(0, time), [time]);

  const [countdownState, setCountdownState] = useState<CountdownState>({
    isCounting: false,
    currentTime: normalizedTime,
    isFinished: false,
    isStarted: false
  });

  const clearTimer = useCallback(() => {
    if (intervalTimerRef.current) {
      clearInterval(intervalTimerRef.current);
      intervalTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    clearTimer();
    isCountingRef.current = false;
    setCountdownState({
      isCounting: false,
      currentTime: normalizedTime,
      isFinished: false,
      isStarted: false
    });
  }, [normalizedTime, clearTimer]);

  const onFinish = useCallback(() => {
    clearTimer();
    setCountdownState({
      isFinished: true,
      isStarted: false,
      isCounting: false,
      currentTime: normalizedTime
    });
  }, [clearTimer, normalizedTime]);

  const start = useCallback(() => {
    if (isCountingRef.current) return;

    if (normalizedTime <= 0) {
      setCountdownState({
        isFinished: true,
        isStarted: false,
        isCounting: false,
        currentTime: normalizedTime
      });
      return;
    }

    isCountingRef.current = true;
    setCountdownState({
      isFinished: false,
      isStarted: true,
      isCounting: true,
      currentTime: normalizedTime
    });
  }, [normalizedTime]);

  useEffect(() => {
    if (!countdownState.isCounting) return;

    intervalTimerRef.current = setInterval(() => {
      setCountdownState((prevState) => {
        const prevTime = prevState.currentTime;
        const nextTime = Math.max(0, prevTime - 1);
        if (nextTime === prevTime) return prevState;

        return {
          ...prevState,
          currentTime: nextTime
        };
      });
    }, 1000);

    return clearTimer;
  }, [countdownState.isCounting, clearTimer]);

  useEffect(() => {
    if (countdownState.isCounting && countdownState.currentTime <= 0) {
      isCountingRef.current = false;
      onFinish();
    }
  }, [countdownState.isCounting, countdownState.currentTime, onFinish]);

  return {
    ...countdownState,
    start
  };
};
